import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  contarRevisionPendientes,
  listarBandejaRevision,
  obtenerExpediente,
  revisarDocumento,
  type ListarBandejaRevisionParams,
  type RevisarDocumentoBody,
} from "../services/revision-documental.api";

/** Bandeja paginada server-side · los filtros forman parte de la key (prefijo común para invalidar). */
export const bandejaRevisionQueryOptions = (params: ListarBandejaRevisionParams = {}) =>
  queryOptions({
    queryKey: [...qk.bandejaRevisionDocumental, "paginado", params] as const,
    queryFn: () => listarBandejaRevision(params),
    placeholderData: keepPreviousData,
  });

export function useBandejaRevision(params: ListarBandejaRevisionParams = {}) {
  return useQuery(bandejaRevisionQueryOptions(params));
}

/**
 * ¿El usuario revisa documentos? y cuántos le tocan · alimenta la entrada del menú y su badge.
 * Quién revisa lo decide la configuración documental (roles revisores), no un permiso fijo.
 */
export const revisionPendientesQueryOptions = queryOptions({
  queryKey: qk.revisionDocumentalPendientes,
  queryFn: contarRevisionPendientes,
  staleTime: 30_000,
});

export function useRevisionPendientes() {
  return useQuery({ ...revisionPendientesQueryOptions, refetchInterval: 60_000 });
}

export const expedienteQueryOptions = (solicitudId: string) =>
  queryOptions({
    queryKey: qk.expediente(solicitudId),
    queryFn: () => obtenerExpediente(solicitudId),
  });

export function useExpediente(solicitudId: string) {
  return useQuery(expedienteQueryOptions(solicitudId));
}

/** Aprueba u observa un documento · refresca expediente, bandeja, contador y la solicitud. */
export function useRevisarDocumento(solicitudId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentoId, body }: { documentoId: string; body: RevisarDocumentoBody }) =>
      revisarDocumento(solicitudId, documentoId, body),
    onSuccess: (_, { body }) => {
      void qc.invalidateQueries({ queryKey: qk.expediente(solicitudId) });
      void qc.invalidateQueries({ queryKey: qk.bandejaRevisionDocumental });
      void qc.invalidateQueries({ queryKey: qk.solicitud(solicitudId) });
      toastExito(
        body.aprobar
          ? { title: "Documento aprobado", message: "Se registró la revisión." }
          : { title: "Documento observado", message: "El solicitante recibirá la observación." }
      );
    },
    onError: (err) => toastErrorApi(err),
  });
}
